import React, { useEffect, useState } from "react";
import { useKeycloakToken } from '../common/useKeycloak';
import AddConnectionDetails from "./AddConnectionDetails";
import { useNavigate, useParams } from 'react-router-dom';
import { Connection, buildDestination, Destination, DestinationType, NewIntegration, Organization, ProviderType, buildProviderType, buildNewIntegration, ConnectionConfiguration } from "../common/ObjectTypes";
import { addConnectionDestinations, createNewConnection, getConnectionDetails } from "../../api/connections/connectionsApi";
import Spinner from "../common/Spinner";
import { getDestinationDetails, getDestinationsList } from "../../api/destinations/destinationsApi";
import { filterDestinationOwner } from "../destinations/DestinationHelperFunctions";
import { toast } from "react-toastify";
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { page_size } from "../../api/apiUtils";
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import { loadProviderTypes } from '../../api/connections/loadProviderTypesNewConnection';
import { useTranslation } from "react-i18next";
import { ManageDestinationDetails } from "../destinations/ManageDestinationDetails";
import NewConnectionStepThree from "./NewConnectionStepThree";
import { useKeycloak } from "@react-keycloak/web";
import ConnectionProgressIndicator from "./NewConnectionProgressIndicator/ConnectionProgressIndicator";
import SettingUpModal from "./NewConnectionProgressIndicator/SettingUpModal";
import { containerClasses, buttonClasses, textClasses } from "../common/tailwindClassnames";

function useScrollLock(lock: boolean) {
    useEffect(() => {
        if (lock) {
            const original = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = original; };
        }
    }, [lock]);
}

export function ManageAddConnectionStepThree() {
    type FilterTypes = {
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    function createFilterTypes(
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    ): FilterTypes {
        return {
            endpoint__in: endpoint__in || [],
            enabled: enabled || [],
            type__in: type__in || [],
            owner__in: owner__in || [],
            ordering: ordering || "",
            search: search || ""
        }
    }
    type FilterTags = {
        name: string,
        id: string
    };
    const { t } = useTranslation();
    const { connection_id } = useParams();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak, initialized } = useKeycloak();
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('newConnectionDestinationFilter');
        return savedFilter ? JSON.parse(savedFilter) : createFilterTypes([], [], [], [], "", "")
    });
    const [initialConnection, setInitialConnection] = useState<NewIntegration>(buildNewIntegration(true))
    const [createdConnection, setCreatedConnection] = useState<Connection>()
    const [errors, setErrors] = useState({});
    const [rsjfErrors, setRsjfErrors] = useState<any[]>([]);
    const [routeId, setRouteId] = useState("");
    const [showSettingUpModal, setShowSettingUpModal] = useState(false);
    // Scroll to top and lock scroll when modal is shown
    useEffect(() => {
        if (showSettingUpModal) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [showSettingUpModal]);
    useScrollLock(showSettingUpModal);

    const getSaveButtonClassName = (step: number, disabled: boolean): string => {
        if (step === 1) {
            return disabled
                ? "bg-gray-lines dark:bg-neutral-gray-700 text-white dark:text-neutral-gray-400 transition-colors duration-300"
                : "bg-dark-green hover:bg-status-green text-white transition-colors duration-300";
        }
        if (step === 2) {
            return "bg-dark-green hover:bg-status-green text-white transition-colors duration-300";
        }
        if (step === 3) {
            return "bg-dark-green hover:bg-status-green text-white transition-colors duration-300";
        }
        return "";
    };
    /* 
        CONNECTION DESTINATIONS:
            - "destinations" array is for destinations already associated with the connection
            - "existingDestPhase0" is for existing destinations that are available to add and unselected
            - "existingDestPhase1" is for existing destinations that have been checked and selected to add (yet to be sorted)
            - "existingDestPhase2" is for selected destinations that are sorted to the top of the available list so they remain there
            regardless of search/filter results
            - "existingDestPhase3" is for unselected destination that are still in their sorted position at the top of the available list
    */
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [existingDestPhase0, setExistingDestPhase0] = useState<Destination[]>(() => {
        const savedDestinations = sessionStorage.getItem('existingDestPhase0');
        return savedDestinations ? JSON.parse(savedDestinations) : []
    });
    const [existingDestPhase1, setExistingDestPhase1] = useState<Destination[]>(() => {
        const savedDestinations = sessionStorage.getItem('existingDestPhase1');
        return savedDestinations ? JSON.parse(savedDestinations) : []
    });
    const [existingDestPhase2, setExistingDestPhase2] = useState<Destination[]>(() => {
        const savedDestinations = sessionStorage.getItem('existingDestPhase2');
        return savedDestinations ? JSON.parse(savedDestinations) : []
    });
    const [existingDestPhase3, setExistingDestPhase3] = useState<Destination[]>(() => {
        const savedDestinations = sessionStorage.getItem('existingDestPhase3');
        return savedDestinations ? JSON.parse(savedDestinations) : []
    });
    const [reloadDestinations, setReloadDestinations] = useState(true);

    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [reloadProviderTypes, setReloadProviderTypes] = useState(true);
    const [providerTypesOffset, setProviderTypesOffset] = useState<number>(0);
    const [providerTypesTotal, setProviderTypesTotal] = useState<number>(0);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [destinationTypesOffset, setDestinationTypesOffset] = useState<number>(0);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);
    const [loadMoreDestinationTypes, setLoadMoreDestinationTypes] = useState(true);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [loadMoreOrganizations, setLoadMoreOrganizations] = useState(true);
    const [organizationOffset, setOrganizationOffset] = useState<number>(0);
    const [destDetailsModal, setDestDetailsModal] = useState<{ isOpen: boolean; destId: string | null }>({
        isOpen: false,
        destId: null,
    });

    const [selectedProviderType, setSelectedProviderType] = useState<ProviderType>(buildProviderType());
    const [destinationOffset, setDestinationOffset] = useState<number>(0);
    const [isProviderTypeSelected, setIsProviderTypeSelected] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [currentStep, setCurrentStep] = useState(3);
    const navigate = useNavigate();
    // Remove addDestModal state, not needed for page navigation
    const [addExistingDest, setAddExistingDest] = useState(sessionStorage.getItem("addExistingDest") ? sessionStorage.getItem("addExistingDest") === "true" ? true : false : false);
    const [updatingDestinations, setUpdatingDestinations] = useState(false);

    const loadConnectionDetails = async () => {

        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && connection_id) {
            try {
                const tempConnection = await getConnectionDetails(token, connection_id);
                setRouteId(tempConnection.default_route.id);
                setDestinationFilter((prevFilter: any) => ({
                    ...prevFilter,
                    owner__in: tempConnection.owner ? [{ name: tempConnection.owner.name, id: tempConnection.owner.id }] : [],
                }));
                setCreatedConnection(tempConnection);
                setPageLoaded(true);
            } catch (err) {
                console.log(err);
            }
        }
    }

    const loadOrganizationsData = async (offset: number, search_str: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            const [orgs, total_orgs, error] = await loadOrganizations(token, offset, search_str, organizations);
            setLoadingOrganizations(true);
            if (!error) {
                setOrganizations(orgs);
                if (offset + page_size >= total_orgs) {
                    setLoadMoreOrganizations(false);
                }
                setLoadingOrganizations(false);
            }
        }
    }

    const loadDestinationTypesData = async (offset: number, search_str: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            const [dest_types, total_dest_types, error] = await loadDestinationTypes(token, offset, search_str, destinationTypes);
            setLoadingDestinationTypes(true);
            if (!error) {
                setDestinationTypes(dest_types);
                if (offset + page_size >= total_dest_types) {
                    setLoadMoreDestinationTypes(false);
                }
                setLoadingDestinationTypes(false);
            }
        }
    }
    const loadProviderTypesData = async (offset: number, search_str: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setReloadProviderTypes(true);
            const [provider_types, total_provider_types, error] = await loadProviderTypes(token, offset, search_str, providerTypes);
            if (!error) {
                setProviderTypes(provider_types);
                setProviderTypesTotal(total_provider_types);
                setReloadProviderTypes(false);
            }
            setPageLoaded(true);

        }
    }

    useEffect(() => {
        sessionStorage.setItem('newConnectionDestinationFilter', JSON.stringify(destinationFilter));
        const loadDestinations = async (destArray: Destination[]) => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                setExistingDestPhase2(existingDestPhase2.concat(existingDestPhase1));
                setExistingDestPhase3(existingDestPhase2.concat(existingDestPhase1));
                sessionStorage.setItem("existingDestPhase2", JSON.stringify(existingDestPhase2.concat(existingDestPhase1)));
                sessionStorage.setItem("existingDestPhase3", JSON.stringify(existingDestPhase2.concat(existingDestPhase1)));
                setUpdatingDestinations(true);
                try {
                    const destinationFilterQuery = {
                        endpoint__in: destinationFilter.endpoint__in.map(a => a.id).toString(),
                        enabled: destinationFilter.enabled.map(a => a.id).toString(),
                        type__in: destinationFilter.type__in.map(a => a.id).toString(),
                        owner__in: destinationFilter.owner__in.map(a => a.id).toString(),
                        ordering: destinationFilter.ordering,
                        search: destinationFilter.search,
                        offset: destinationOffset,
                        previous: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (destinationOffset - page_size),
                        next: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (destinationOffset + page_size)
                    }
                    const temp = await getDestinationsList(token, destinationFilterQuery);
                    // check to see if destination in temp.result is in the selected array
                    const tempDestArray: Destination[] = temp.results.filter((b: Destination) => !destArray.map((a: Destination) => a.id).includes(b.id))

                    setExistingDestPhase0(tempDestArray);
                    sessionStorage.setItem("existingDestPhase0", JSON.stringify(tempDestArray));
                    sessionStorage.setItem("existingDestPhase1", JSON.stringify([]));
                    setExistingDestPhase1([]);
                    setUpdatingDestinations(false);
                    setReloadDestinations(false);
                } catch (err) {
                    console.log(err)
                }
            }
        }


        if (reloadProviderTypes) loadProviderTypesData(providerTypesOffset, "");

        if (reloadDestinations) loadDestinations([...destinations, ...existingDestPhase1, ...existingDestPhase2]);
        loadOrganizationsData(organizationOffset, "");
        loadDestinationTypesData(destinationTypesOffset, "");
        if (!pageLoaded) loadConnectionDetails();
    }, [token, reloadDestinations, providerTypes])


    const handleClickAddDestinationsStep = () => {
        setShowSettingUpModal(true);
    }
    const handleCloseModalAndAddDestinations = () => {
        setShowSettingUpModal(false);
        handleAddSelectedExistingDestinations();
    }
    const handleCloseModalAndStayOnPage = () => {
        setShowSettingUpModal(false);
    }

    const handleToggleDestinationDetailsModal = async (destId?: string) => {
        if (!destId) {
            // Close modal and remove destination details from URL
            setDestDetailsModal({ isOpen: false, destId: null });
            navigate(`/connections/new/${connection_id}/configuredestinations`);
            return;
        }

        // Open modal and update URL
        navigate(`/connections/new/${connection_id}/configuredestinations/destination/${destId}/configuration`);
        if (!destDetailsModal.isOpen || destDetailsModal.destId !== destId) {
            setDestDetailsModal({ isOpen: true, destId: destId });
        }
    };

    const handleSelectExistingDestination = (dest: Destination) => {
        let tempPhase0Array: Destination[] = [...existingDestPhase0];
        let tempPhase1Array: Destination[] = [...existingDestPhase1];
        let tempPhase2Array: Destination[] = [...existingDestPhase2];
        let tempPhase3Array: Destination[] = [...existingDestPhase3];

        const indexInPhase0 = tempPhase0Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase1 = tempPhase1Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase2 = tempPhase2Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase3 = tempPhase3Array.findIndex((a: Destination) => a.id === dest.id);

        if (indexInPhase0 !== -1) {
            if (indexInPhase1 !== -1) {
                // Remove from phase 1
                tempPhase1Array.splice(indexInPhase1, 1);
                sessionStorage.setItem("existingDestPhase1", JSON.stringify(tempPhase1Array));
                setExistingDestPhase1(tempPhase1Array);
            } else {
                // Add to phase 1
                tempPhase1Array.push(dest);
                setExistingDestPhase1(tempPhase1Array);
                sessionStorage.setItem("existingDestPhase1", JSON.stringify(tempPhase1Array));
            }
        }

        if (indexInPhase3 !== -1) {
            if (indexInPhase2 !== -1) {
                // Remove from phase 2
                tempPhase2Array.splice(indexInPhase2, 1);
                setExistingDestPhase2(tempPhase2Array);
                sessionStorage.setItem("existingDestPhase2", JSON.stringify(tempPhase2Array));
            } else {
                // Add to phase 2
                tempPhase2Array.push(dest);
                setExistingDestPhase2(tempPhase2Array);
                sessionStorage.setItem("existingDestPhase2", JSON.stringify(tempPhase2Array));
                // Remove from phase 1
                // Remove from phase 1
                tempPhase1Array.splice(indexInPhase1, 1);
                setExistingDestPhase1(tempPhase1Array);
                sessionStorage.setItem("existingDestPhase1", JSON.stringify(tempPhase1Array));
            }
        }
    }

    const clearExistingDestArrays = () => {
        sessionStorage.removeItem("existingDestPhase0");
        sessionStorage.removeItem("existingDestPhase1");
        sessionStorage.removeItem("existingDestPhase2");
        sessionStorage.removeItem("existingDestPhase3");
        setExistingDestPhase0([]);
        setExistingDestPhase1([]);
        setExistingDestPhase2([]);
        setExistingDestPhase3([]);
    }

    const handleAddSelectedExistingDestinations = async () => {
        const destination_ids = {
            "destinations": existingDestPhase1.map(a => a.id).concat(destinations.map(a => a.id), existingDestPhase2.map(a => a.id))
        }
        setUpdatingDestinations(true);
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await addConnectionDestinations(token, routeId, destination_ids);
                setDestinations(existingDestPhase1.concat(destinations, existingDestPhase2));
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });

                sessionStorage.removeItem("addExistingDest");
                sessionStorage.removeItem("newConnectionDestinationFilter");
                navigate(`/connections/${createdConnection?.id}/logs`, {
                    state: {
                        fromAddConnectionFlow: true,
                        skippedAddingDestinations: false
                    }
                });
                clearExistingDestArrays();
            } catch (error) {
                console.log(error)
            }
        }

        setUpdatingDestinations(false);
        setAddExistingDest(false);
        sessionStorage.setItem("addExistingDest", "false");
    }

    const handleClickAddDestination = (existing: boolean) => {
        if (existing) {
            clearExistingDestArrays()
            setReloadDestinations(true);
            setAddExistingDest(!addExistingDest)
            sessionStorage.setItem("addExistingDest", (!addExistingDest).toString());
        } else {
            // Navigate to add destination page for this connection
            navigate(`/connections/new/${connection_id}/configuredestinations/newdestination`);
        }
    }

    const handleChangeDestinationStateFilter = (e: any) => {
        let tempArray: FilterTags[] = destinationFilter.enabled
        // active was toggled
        if (e.target.value === "active") {
            if (tempArray.map(a => a.id).includes("true")) {
                let removed: FilterTags[] = []
                for (var item in tempArray) {
                    if (tempArray[item].id !== "true") {
                        removed.push(tempArray[item])
                    }
                    setDestinationFilter((prevFilter: any) => ({
                        ...prevFilter,
                        [e.target.id]: removed
                    }))
                }
            } else {
                const newItem = {
                    name: "Active",
                    id: "true",
                    attribute_name: "enabled"
                }
                tempArray.push(newItem);
                setDestinationFilter((prevFilter: any) => ({
                    ...prevFilter,
                    [e.target.id]: tempArray
                }))
            }

        } else { // inactive was toggled
            if (tempArray.map(a => a.id).includes("false")) {
                let removed: FilterTags[] = []
                for (item in tempArray) {
                    if (tempArray[item].id !== "false") {
                        removed.push(tempArray[item])
                    }
                    setDestinationFilter((prevFilter: any) => ({
                        ...prevFilter,
                        [e.target.id]: removed
                    }))
                }
            } else {
                const newItem = {
                    name: "Inactive",
                    id: "false",
                    attribute_name: "enabled"
                }
                tempArray.push(newItem);
                setDestinationFilter((prevFilter: any) => ({
                    ...prevFilter,
                    [e.target.id]: tempArray
                }))
            }
        }
        setReloadDestinations(true);
    }

    const handleChangeDestinationTypeSearchBar = (e: any) => {
        if (e.target.value.length >= 3 || e.target.value.length === 0) {
            loadDestinationTypesData(destinationTypesOffset, e.target.value);
        }
    }

    const handleLoadMoreDestinationTypes = () => {
        setDestinationTypesOffset(destinationTypesOffset + page_size);
        loadDestinationTypesData(destinationTypesOffset + page_size, "");
    }

    const handleChangeDestinationTypeFilter = (e: any) => {
        let tempArray: FilterTags[] = destinationFilter.type__in
        if (tempArray.map(a => a.id).includes(e.target.value)) {
            let removed: FilterTags[] = []
            for (var item in tempArray) {
                if (tempArray[item].id !== e.target.value) {
                    removed.push(tempArray[item])
                }

            }
            setDestinationFilter((prevFilter: any) => ({
                ...prevFilter,
                [e.target.id]: removed
            }))
        } else {
            const newTag = {
                name: e.target.name,
                id: e.target.value
            }
            tempArray.push(newTag)
            setDestinationFilter((prevFilter: any) => ({
                ...prevFilter,
                [e.target.id]: tempArray
            }))
        }
        setReloadDestinations(true);
    }

    const handleChangeDestinationUrlSearchBar = (e: any) => {
        console.log(e.target.value)
    }

    const handleChangeDestinationOwnerFilter = (e: any) => {
        let tempArray: FilterTags[] = destinationFilter.owner__in
        if (tempArray.map(a => a.id).includes(e.target.value)) {
            let removed: FilterTags[] = []
            for (var item in tempArray) {
                if (tempArray[item].id !== e.target.value) removed.push(tempArray[item])
            }
            setDestinationFilter((prevFilter: any) => ({
                ...prevFilter,
                [e.target.id]: removed
            }))
        } else {
            const newTag = {
                name: e.target.name,
                id: e.target.value
            }
            tempArray.push(newTag)
            setDestinationFilter((prevFilter: any) => ({
                ...prevFilter,
                [e.target.id]: tempArray
            }))
        }
        setReloadDestinations(true);
    }


    // Helper: check if any destination in phase 1, 2, or 3 matches the search term (name or base_url)
    const searchTerm = destinationFilter.search || "";
    const lowerSearch = searchTerm.trim().toLowerCase();
    function matchesSearch(dest: Destination) {
        return (
            dest.name.toLowerCase().includes(lowerSearch) ||
            dest.base_url.toLowerCase().includes(lowerSearch)
        );
    }
    const hasOtherPhaseResultsInSearch =
        !!lowerSearch && ([...existingDestPhase1, ...existingDestPhase2, ...existingDestPhase3].some(matchesSearch));

    const handleGlobalDestinationSearch = (searchSubStr: string) => {
        setDestinationFilter((prevFilter: any) => ({
            ...prevFilter,
            search: searchSubStr
        }));
        setReloadDestinations(true);
    }

    const handleRemoveDestinationFilterTag = (e: any) => {
        if (destinationFilter.type__in.map(a => a.id).includes(e)) {
            let tempArray: FilterTags[] = destinationFilter.type__in
            let removed: FilterTags[] = []
            for (var item in tempArray) {
                if (tempArray[item].id !== e) removed.push(tempArray[item])
            }
            setDestinationFilter((prevFilter: any) => ({
                ...prevFilter,
                type__in: removed
            }))
        } else if (destinationFilter.owner__in.map(a => a.id).includes(e)) {
            let tempArray: FilterTags[] = destinationFilter.owner__in
            let removed: FilterTags[] = []
            for (item in tempArray) {
                if (tempArray[item].id !== e) removed.push(tempArray[item])
            }
            setDestinationFilter((prevFilter: any) => ({
                ...prevFilter,
                owner__in: removed
            }))
        } else if ((destinationFilter.endpoint__in.map(a => a.id).includes(e))) {
            let tempArray: FilterTags[] = destinationFilter.endpoint__in
            let removed: FilterTags[] = []
            for (item in tempArray) {
                if (tempArray[item].id !== e) removed.push(tempArray[item])
            }
            setDestinationFilter((prevFilter: any) => ({
                ...prevFilter,
                endpoint__in: removed
            }))
        } else {
            // enabled
            let tempArray: FilterTags[] = destinationFilter.enabled
            let removed: FilterTags[] = []
            for (item in tempArray) {
                if (tempArray[item].id !== e) removed.push(tempArray[item])
            }
            setDestinationFilter((prevFilter: any) => ({
                ...prevFilter,
                enabled: removed
            }))
        }
        setReloadDestinations(true);
    }

    const handleResetDestinationFilters = (e: any) => {
        setDestinationFilter({
            endpoint__in: [],
            enabled: [],
            type__in: [],
            owner__in: [],
            ordering: "",
            search: ""
        });
        setReloadDestinations(true);
    }

    const formIsValid = () => {
        let form_errors: any = {};
        let rsjf_errors: any = {};
        if (initialConnection.name.length === 0) form_errors.name = "Name is required."
        if (initialConnection.name.length > 50) form_errors.name = "Name cannot exceed 50 characters.";
        if (initialConnection.base_url.length > 0) {
            try {
                new URL(initialConnection.base_url);
            } catch (err) {
                form_errors.base_url = "Enter a valid url including the scheme."
            }
        }
        if (initialConnection.owner === "") form_errors.owner = "Select a workspace."

        setErrors(form_errors)

        setRsjfErrors(rsjf_errors);
        return Object.keys(form_errors).length === 0 && Object.keys(rsjf_errors).length === 0;
    }

    const handleCreateConnection = async (connectionToAdd: NewIntegration) => {
        if (!formIsValid()) return;
        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && formIsValid()) {
            const temp = await createNewConnection(token, connectionToAdd);
            setCreatedConnection(temp)
            setRouteId(temp.default_route.id);
            toast.success(`${t("New connection made, ready to add destinations")}`, {
                position: toast.POSITION.TOP_RIGHT
            });
        }
    }

    const handleChangeOrganizationSearchBar = (e: any) => {
        if (e.target.value.length >= 3 || e.target.value.length === 0) {
            loadOrganizationsData(organizationOffset, e.target.value);
        }
    }

    const handleLoadMoreOrganizations = () => {
        setOrganizationOffset(organizationOffset + page_size);
        loadOrganizationsData(organizationOffset + page_size, "");
    }

    const handleCancel = () => {
        setShowSettingUpModal(false);
        navigate("/connections/" + createdConnection?.id + "/logs", {
            state: {
                fromAddConnectionFlow: true,
                skippedAddingDestinations: true
            }
        });
    }

    return !pageLoaded ?
        (
            <Spinner />
        ) : (
            <div>
                {showSettingUpModal && <SettingUpModal
                    selectedProviderType={selectedProviderType.name}
                    onCloseAndAddDestinations={handleCloseModalAndAddDestinations}
                    onCloseAndStayOnPage={handleCloseModalAndStayOnPage}
                />}
                {destDetailsModal.isOpen &&
                    <ManageDestinationDetails />
                }
                <div className="bg-white dark:bg-off-black shadow static inset-x-0 px-12 pt-5 pb-1 -mt-5 -mx-5 xl:-mx-12 transition-colors duration-300">
                    <h1 className="text-3xl font-semibold mb-3 text-off-black dark:text-white transition-colors duration-300">{t("New")} {selectedProviderType.name} {t("Connection")}</h1>
                </div>
                <div className="px-12 pt-5 -mt-5 -mx-5 xl:-mx-12">
                    <ConnectionProgressIndicator
                        stepOneStatus="completed"
                        stepTwoStatus="in-progress"
                        stepThreeStatus="upcoming"
                        completed={false}
                    />
                    <NewConnectionStepThree
                        destinations={destinations}
                        existingDestPhase0={existingDestPhase0}
                        existingDestPhase1={existingDestPhase1}
                        existingDestPhase2={existingDestPhase2}
                        existingDestPhase3={existingDestPhase3}
                        onSelectExistingDestination={handleSelectExistingDestination}
                        destinationTypes={destinationTypes}
                        organizations={organizations}
                        filters={destinationFilter.endpoint__in
                            .concat(destinationFilter.type__in, destinationFilter.owner__in, destinationFilter.enabled)}
                        onChangeStateFilter={handleChangeDestinationStateFilter}
                        onChangeTypeFilter={handleChangeDestinationTypeFilter}
                        onChangeDestinationTypeSearchBar={handleChangeDestinationTypeSearchBar}
                        loadingDestinationTypes={loadingDestinationTypes}
                        loadMoreDestinationTypes={loadMoreDestinationTypes}
                        onLoadMoreDestinationTypes={handleLoadMoreDestinationTypes}
                        onChangeOwnerFilter={handleChangeDestinationOwnerFilter}
                        onChangeOwnerSearchBar={handleChangeOrganizationSearchBar}
                        onLoadMoreOrganizations={handleLoadMoreOrganizations}
                        loadingOrganizations={loadingOrganizations}
                        loadMoreOrganizations={loadMoreOrganizations}
                        onChangeDestinationUrlSearchBar={handleChangeDestinationUrlSearchBar}
                        onGlobalDestinationFilter={handleGlobalDestinationSearch}
                        onRemoveDestinationFilterTag={handleRemoveDestinationFilterTag}
                        onResetDestinationFilters={handleResetDestinationFilters}
                        pageLoaded={pageLoaded}
                        addExistingDest={addExistingDest}
                        updatingDestinations={updatingDestinations}
                        onClickAddDestination={handleClickAddDestination}
                        onAddExistingDestinations={handleAddSelectedExistingDestinations}
                        onViewDetails={handleToggleDestinationDetailsModal}
                        searchTerm={searchTerm}
                    />
                </div>

                <div className="fixed flex flex-row bottom-0 right-0 pr-5 py-3 pl-16 bg-white/10 dark:bg-dark-gray/10 backdrop-blur w-full shadow-lg transition-colors duration-300">
                    <button className={`px-8 py-2 font-bold rounded mr-3 transition-colors duration-300 ${getSaveButtonClassName(currentStep, !isProviderTypeSelected)}`}
                        onClick={() => handleClickAddDestinationsStep()}
                    >
                        {t("Add Destinations")}
                    </button>
                    <button className="text-secondary-gray dark:text-gray-lines font-bold rounded py-2 px-3 mx-2 hover:text-dark-green dark:hover:text-white transition-colors duration-300"
                        type="button"
                        onClick={() => handleCancel()}
                    >
                        {t("Skip Step")}
                    </button>
                </div>
            </div>
        )
}